import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { parlayToWin } from '../utils/oddsCalculations';
import { FaPlus, FaTimes } from 'react-icons/fa';
import usePageTitle from '../hooks/usePageTitle';
import PageMeta from '../components/SEO/PageMeta';

const Container = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 60vh;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${(props) => props.theme.primaryText};
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: ${(props) => props.theme.secondaryText};
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const Card = styled.div`
  background: ${(props) => props.theme.cardBg};
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: ${(props) => props.theme.shadow};
  border: 1px solid ${(props) => props.theme.border};
  margin-bottom: 1rem;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 100px;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(props) => props.theme.secondaryText};
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  font-size: 1rem;
  border-radius: 0.35rem;
  border: 1px solid ${(props) => props.theme.border};
  background: ${(props) => props.theme.inputBg};
  color: ${(props) => props.theme.primaryText};
  width: 100%;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.primary};
  }
  &::placeholder {
    color: ${(props) => props.theme.mutedText};
  }
`;

const LegsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const LegWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const LegInput = styled(Input)`
  width: 90px;
`;

const RemoveLegBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 0.25rem;
  background: ${(props) => props.theme.secondaryBg};
  color: ${(props) => props.theme.secondaryText};
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    background: ${(props) => props.theme.tertiaryBg};
    color: ${(props) => props.theme.primary};
  }
`;

const AddBetBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 0.35rem;
  border: 1px dashed ${(props) => props.theme.border};
  background: transparent;
  color: ${(props) => props.theme.primary};
  cursor: pointer;
  &:hover {
    background: ${(props) => props.theme.tertiaryBg};
    border-color: ${(props) => props.theme.primary};
  }
`;

const OutputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid ${(props) => props.theme.border};
`;

const OutputBox = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 0.35rem;
  background: ${(props) => props.theme.secondaryBg || 'rgba(255,255,255,0.06)'};
  border: 1px solid ${(props) => props.theme.border};
`;

const OutputValue = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryText};
`;

const ResetBtn = styled.button`
  display: block;
  margin: 1.25rem auto 0;
  padding: 0.5rem 1.25rem;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 0.35rem;
  border: 1px solid ${(props) => props.theme.border};
  background: ${(props) => props.theme.secondaryBg};
  color: ${(props) => props.theme.secondaryText};
  cursor: pointer;
  &:hover {
    background: ${(props) => props.theme.tertiaryBg};
    color: ${(props) => props.theme.primaryText};
  }
`;

const DEFAULT_LEGS = ['-150', '-125', '-125'];

const parseAmerican = (str) => {
  const s = String(str).trim();
  if (s === '') return null;
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? null : n;
};

const formatAmerican = (n) => {
  if (n == null || !Number.isFinite(n)) return '';
  return n >= 0 ? `+${n}` : String(n);
};

const ParlayCalculatorPage = () => {
  const { theme } = useTheme();
  usePageTitle('Parlay Calculator – Combined Odds & Winnings');
  const [betAmount, setBetAmount] = useState('10');
  const [legs, setLegs] = useState(DEFAULT_LEGS);

  const stake = parseFloat(betAmount) || 0;
  const americanLegs = legs.map((s) => parseAmerican(s)).filter((a) => a != null && a !== 0);
  const { parlayAmerican, toWin: toWinAmount } = parlayToWin(americanLegs, stake);

  const updateLeg = (index, value) => {
    setLegs((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const removeLeg = (index) => {
    if (legs.length <= 1) return;
    setLegs((prev) => prev.filter((_, i) => i !== index));
  };

  const addLeg = () => {
    setLegs((prev) => [...prev, '']);
  };

  const handleReset = () => {
    setBetAmount('10');
    setLegs(DEFAULT_LEGS);
  };

  return (
    <Container>
      <PageMeta description="Calculate combined American odds and potential winnings for multi-leg parlays using Slick Picks' parlay calculator, then plug in legs from today’s MLB picks." />
      <Title theme={theme}>Parlay Calculator</Title>
      <Subtitle theme={theme}>
        Input the odds for each leg of your parlay to quickly calculate combined odds and payouts. Build parlays
        from today&apos;s picks on the{' '}
        <a href="/game-picks">Game Picks</a> page or from model edges like{' '}
        <a href="/mlb/total-bases">Total Bases</a> and <a href="/mlb/hits">Hits</a>.
      </Subtitle>

      <Card theme={theme}>
        <Row>
          <Field>
            <Label theme={theme}>Bet Amount</Label>
            <Input
              theme={theme}
              type="number"
              min="0"
              step="0.01"
              value={betAmount}
              onChange={(e) => setBetAmount(e.target.value)}
              placeholder="0"
            />
          </Field>
        </Row>

        <Label theme={theme} style={{ marginBottom: '0.5rem' }}>Legs (American odds)</Label>
        {legs.map((value, index) => (
          <LegsRow key={index}>
            <LegWrap>
              <LegInput
                theme={theme}
                type="text"
                value={value}
                onChange={(e) => updateLeg(index, e.target.value)}
                placeholder={index === 0 ? '-150' : ''}
                aria-label={`Bet ${index + 1}`}
              />
              {legs.length > 1 && (
                <RemoveLegBtn
                  theme={theme}
                  type="button"
                  onClick={() => removeLeg(index)}
                  aria-label={`Remove bet ${index + 1}`}
                >
                  <FaTimes size={14} />
                </RemoveLegBtn>
              )}
            </LegWrap>
          </LegsRow>
        ))}
        <AddBetBtn theme={theme} type="button" onClick={addLeg}>
          <FaPlus size={14} aria-hidden />
          Add Bet
        </AddBetBtn>

        <OutputRow theme={theme}>
          <OutputBox theme={theme}>
            <Label theme={theme}>Parlay Odds</Label>
            <OutputValue theme={theme}>
              {americanLegs.length > 0 ? formatAmerican(parlayAmerican) : '—'}
            </OutputValue>
          </OutputBox>
          <OutputBox theme={theme}>
            <Label theme={theme}>To Win</Label>
            <OutputValue theme={theme}>
              $ {americanLegs.length > 0 ? toWinAmount.toFixed(2) : '0.00'}
            </OutputValue>
          </OutputBox>
        </OutputRow>

        <ResetBtn theme={theme} type="button" onClick={handleReset}>
          Reset
        </ResetBtn>
      </Card>
    </Container>
  );
};

export default ParlayCalculatorPage;
